import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { Link } from "react-router-dom";

import Eta from "../../components/general/Eta";
import Banner from "../../components/general/Banner";
import ProductItem from "./ProductItem";

import { fetchContent } from "../../redux/slices/contentSlice";
import { fetchContact } from "../../redux/slices/contactSlice";
import { fetchProduct } from "../../redux/slices/productSlice";
import { fetchProductDetail } from "../../redux/slices/productDetailSlice";
import { fetchSeo } from "../../redux/slices/seoSlice";

const ProductFamily = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const location = useLocation();
  const lang = useSelector((state) => state.lang.lang);
  const { dataContent } = useSelector((state) => state.content);
  const { dataContact } = useSelector((state) => state.contact);
  const { dataProduct: products } = useSelector((state) => state.product);
  const { dataProductDetail: productDetails } = useSelector(
    (state) => state.productDetail
  );
  const { dataSeo } = useSelector((state) => state.seo);
  const [seoData, setSeoData] = useState(null);
  const [isDataLoaded, setIsDataLoaded] = useState(false);

  let bannerText = "";
  let product_family_header = "";
  let mail = "";
  let product_info = "";
  if (dataContent) {
    bannerText = dataContent.bannerText?.[lang] || "";
    product_info = dataContent.specialField5?.[lang] || "";
    product_family_header = dataContent.showcaseText2?.[lang] || "";
  }
  if (dataContact) {
    mail = dataContact.mail || "";
  }

  useEffect(() => {
    const fetchData = async () => {
      await Promise.all([
        dispatch(fetchContent()),
        dispatch(fetchProduct()),
        dispatch(fetchContact()),
        dispatch(fetchSeo()),
      ]);
    };

    fetchData();
  }, [dispatch]);

  useEffect(() => {
    if (products?.length > 0) {
      products.forEach((product) => {
        dispatch(fetchProductDetail(product.id));
      });
    }
  }, [products, dispatch]);

  useEffect(() => {
    const setSeoContent = async () => {
      if (dataSeo?.length > 0) {
        const productFamilyData = dataSeo.find(
          (item) =>
            item.title[lang] === "Ürün Aileleri" ||
            item.title[lang] === "Product Families"
        );
        if (productFamilyData) {
          setSeoData(productFamilyData);
          document.title = productFamilyData.title[lang];
        }
      }
    };

    setSeoContent();
  }, [dataSeo, lang]);

  useEffect(() => {
    if (
      products?.length > 0 &&
      Array.isArray(productDetails) &&
      productDetails.length > 0 &&
      dataSeo?.length > 0 &&
      dataContent
    ) {
      setIsDataLoaded(true);
    }
  }, [products, productDetails, dataSeo, dataContent]);

  useEffect(() => {
    const scrollToProduct = () => {
      if (
        location.state?.productId &&
        location.state?.scrollToProduct &&
        isDataLoaded
      ) {
        const element = document.getElementById(
          `product${location.state.productId}`
        );
        if (element) {
          // Sayfayı en üstte başlat ve direkt olarak hedef elemana scroll yap
          window.scrollTo(0, 0);
          setTimeout(() => {
            const headerOffset = 100; // Header yüksekliğine göre ayarlayın
            const elementPosition = element.getBoundingClientRect().top;
            const offsetPosition =
              elementPosition + window.pageYOffset - headerOffset;

            window.scrollTo({
              top: offsetPosition,
              behavior: "smooth",
            });
          }, 300);
        }
      }
    };

    scrollToProduct();
  }, [isDataLoaded, location.state]);

  const sortedProducts = products
    ? [...products].sort((a, b) => a.sort - b.sort)
    : [];

  return (
    <Eta>
      {seoData && (
        <Helmet>
          <title>{seoData.title[lang]}</title>
          <meta name="description" content={seoData.description[lang]} />
          {seoData.keywords[lang] && (
            <meta name="keywords" content={seoData.keywords[lang]} />
          )}
          {seoData.canonical && (
            <link rel="canonical" href={seoData.canonical} />
          )}
          {seoData.author && <meta name="author" content={seoData.author} />}
        </Helmet>
      )}
      <Banner title={bannerText} />

      <div className="container mt-4 mb-5 text-center">
        <h2 className="mb-3">{product_family_header}</h2>
      </div>

      <div className="product-family-page">
        {sortedProducts.length > 0 ? (
          sortedProducts.map((product) => {
            const details = Array.isArray(productDetails)
              ? productDetails.filter(
                  (detail) => detail.productID === product.id
                )
              : [];

            const sortedDetails = [...details].sort((a, b) => a.sort - b.sort);

            const images = [
              ...new Set(
                sortedDetails.reduce((acc, detail) => {
                  if (detail.files && Array.isArray(detail.files)) {
                    acc.push(...detail.files);
                  }
                  return acc;
                }, [])
              ),
            ];

            return (
              <div
                key={product.id}
                id={`product${product.id}`}
                className="scroll-margin-top" // Scroll için margin ekleyelim
              >
                <ProductItem
                  title={product.title?.[lang] || ""}
                  description={product.description?.[lang] || ""}
                  subtitle={sortedDetails.map(
                    (detail) => detail.title?.[lang] || ""
                  )}
                  text={sortedDetails.map(
                    (detail) => detail.description?.[lang] || ""
                  )}
                  img={images}
                />
              </div>
            );
          })
        ) : (
          <div>Ürün bulunamadı</div>
        )}

        <div className="border-top">
          <div className="mt-3 d-flex justify-content-center align-items-center color6">
            {product_info}&nbsp;
            <Link
              to={`mailto:${mail}`}
              target="_blank"
              className="f16 fw500 color7"
            >
              {mail}
            </Link>
          </div>
        </div>
      </div>
    </Eta>
  );
};

export default ProductFamily;
